<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="secondary"
      class="mb-3"
    />
    <v-alert
      v-if="loadError"
      type="error"
      dense
      outlined
      border="left"
    >
      {{ loadError }}
    </v-alert>

    <template v-if="!loading && !loadError">
      <v-row dense>
        <!-- Roles list -->
        <v-col
          cols="12"
          md="4"
        >
          <v-sheet
            rounded
            outlined
            class="pa-3"
          >
            <div class="d-flex align-center mb-2">
              <v-icon
                color="primary"
                class="mr-2"
              >
                mdi-account-group-outline
              </v-icon>
              <div class="text-subtitle-1">
                Cargos
              </div>
              <v-spacer />
              <v-btn
                icon
                small
                @click="openCreateDialog"
              >
                <v-icon small>
                  mdi-plus
                </v-icon>
              </v-btn>
            </div>
            <v-list
              dense
              nav
            >
              <v-list-item
                v-for="role in cargos"
                :key="role.id"
                :input-value="selected && selected.id === role.id"
                color="secondary"
                @click="select(role)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon
                    small
                    :color="role.administrador ? 'amber darken-2' : 'grey'"
                  >
                    {{ role.administrador ? 'mdi-shield-crown-outline' : 'mdi-shield-account-outline' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ role.nome }}</v-list-item-title>
                  <v-list-item-subtitle v-if="role.descricao">
                    {{ role.descricao }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip
                    :color="role.ativo ? 'success' : 'grey'"
                    x-small
                    dark
                  >
                    {{ role.ativo ? 'ativo' : 'inativo' }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
              <v-list-item v-if="!cargos.length">
                <v-list-item-content class="grey--text text--darken-1">
                  Nenhum cargo cadastrado.
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-sheet>
        </v-col>

        <!-- Permission matrix -->
        <v-col
          cols="12"
          md="8"
        >
          <v-sheet
            rounded
            outlined
            class="pa-3"
          >
            <div
              v-if="!selected"
              class="grey--text text--darken-1 pa-6 text-center"
            >
              <v-icon
                large
                color="grey lighten-1"
              >
                mdi-cursor-pointer
              </v-icon>
              <div class="text-subtitle-1 mt-2">
                Selecione um cargo para editar
              </div>
            </div>

            <template v-else>
              <div class="d-flex align-center mb-3">
                <v-icon
                  color="warning"
                  class="mr-2"
                >
                  mdi-shield-key-outline
                </v-icon>
                <div class="text-subtitle-1">
                  {{ selected.nome }}
                </div>
                <v-spacer />
                <v-btn
                  v-if="!selected.administrador"
                  color="error"
                  text
                  small
                  :loading="saving"
                  @click="remove(selected)"
                >
                  <v-icon
                    left
                    small
                  >
                    mdi-delete
                  </v-icon>
                  Excluir
                </v-btn>
                <v-btn
                  color="secondary"
                  small
                  :loading="saving"
                  class="ml-2"
                  @click="save"
                >
                  <v-icon
                    left
                    small
                  >
                    mdi-content-save
                  </v-icon>
                  Salvar
                </v-btn>
              </div>

              <v-row dense>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="form.nome"
                    label="Nome"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="form.descricao"
                    label="Descrição"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  sm="3"
                >
                  <v-switch
                    v-model="form.ativo"
                    label="Ativo"
                    inset
                    dense
                    hide-details
                  />
                </v-col>
                <v-col
                  cols="6"
                  sm="3"
                >
                  <v-switch
                    v-model="form.administrador"
                    label="Administrador"
                    inset
                    dense
                    hide-details
                    :disabled="selected.administrador"
                  />
                </v-col>
              </v-row>

              <v-divider class="my-3" />

              <v-alert
                v-if="form.administrador"
                type="info"
                dense
                outlined
                border="left"
              >
                Cargos administradores possuem todas as permissões automaticamente.
              </v-alert>

              <v-expansion-panels
                v-else
                multiple
                accordion
                flat
              >
                <v-expansion-panel
                  v-for="grupo in groupedCatalog"
                  :key="grupo.key"
                >
                  <v-expansion-panel-header>
                    <div class="d-flex align-center">
                      <v-icon
                        small
                        color="secondary"
                        class="mr-2"
                      >
                        {{ grupo.icon }}
                      </v-icon>
                      <span class="text-subtitle-2">{{ grupo.label }}</span>
                      <v-spacer />
                      <v-chip
                        x-small
                        outlined
                      >
                        {{ countSelected(grupo) }}/{{ grupo.permissoes.length }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-data-table
                      :headers="matrixHeaders"
                      :items="grupo.permissoes"
                      item-key="id"
                      dense
                      hide-default-footer
                      :items-per-page="-1"
                    >
                      <template #item.descricao="{ item }">
                        <div class="text-body-2">
                          {{ item.descricao }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ item.chave }}
                        </div>
                      </template>
                      <template #item.read="{ item }">
                        <v-checkbox
                          :input-value="hasFlag(item.id, 1)"
                          dense
                          hide-details
                          @change="toggleFlag(item.id, 1, $event)"
                        />
                      </template>
                      <template #item.write="{ item }">
                        <v-checkbox
                          :input-value="hasFlag(item.id, 2)"
                          dense
                          hide-details
                          @change="toggleFlag(item.id, 2, $event)"
                        />
                      </template>
                      <template #item.delete="{ item }">
                        <v-checkbox
                          :input-value="hasFlag(item.id, 4)"
                          dense
                          hide-details
                          @change="toggleFlag(item.id, 4, $event)"
                        />
                      </template>
                    </v-data-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </template>
          </v-sheet>
        </v-col>
      </v-row>
    </template>

    <!-- Create dialog -->
    <v-dialog
      v-model="createDialog.show"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title>Novo cargo</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="createDialog.form.nome"
            label="Nome"
            outlined
            dense
          />
          <v-text-field
            v-model="createDialog.form.descricao"
            label="Descrição"
            outlined
            dense
            class="mt-2"
          />
          <v-switch
            v-model="createDialog.form.administrador"
            label="Administrador (todas as permissões)"
            inset
            dense
            hide-details
          />
          <v-alert
            v-if="createDialog.error"
            type="error"
            dense
            outlined
            border="left"
            class="mt-2"
          >
            {{ createDialog.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="createDialog.saving"
            @click="createDialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="createDialog.saving"
            @click="submitCreate"
          >
            Criar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
  import { $k } from '@/lib/kraken'

  const GROUP_META = {
    organizacao: { label: 'Organização', icon: 'mdi-office-building-outline' },
    agendamento: { label: 'Agendamento', icon: 'mdi-calendar-clock' },
    paciente: { label: 'Paciente', icon: 'mdi-account-injury-outline' },
    faturamento: { label: 'Faturamento', icon: 'mdi-cash-multiple' },
    relatorios: { label: 'Relatórios', icon: 'mdi-file-chart-outline' },
  }

  export default {
    name: 'RolesTab',

    props: {
      org: { type: Object, required: true },
    },

    data: () => ({
      loading: false,
      loadError: '',
      saving: false,
      cargos: [],
      catalog: [],
      selected: null,
      form: { nome: '', descricao: '', ativo: true, administrador: false, permissoes: [] },
      matrixHeaders: [
        { text: 'Permissão', value: 'descricao' },
        { text: 'Ler', value: 'read', width: 70, sortable: false, align: 'center' },
        { text: 'Escrever', value: 'write', width: 90, sortable: false, align: 'center' },
        { text: 'Excluir', value: 'delete', width: 80, sortable: false, align: 'center' },
      ],
      createDialog: {
        show: false,
        saving: false,
        error: '',
        form: { nome: '', descricao: '', administrador: false },
      },
      snackbar: { show: false, color: 'success', text: '' },
    }),

    computed: {
      groupedCatalog () {
        const groups = {}
        for (const perm of this.catalog) {
          const key = perm.grupo
          if (!groups[key]) {
            const meta = GROUP_META[key] || { label: key, icon: 'mdi-shield-outline' }
            groups[key] = { key, label: meta.label, icon: meta.icon, permissoes: [] }
          }
          groups[key].permissoes.push(perm)
        }
        return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label))
      },
    },

    watch: {
      'org.id': {
        immediate: true,
        handler () { this.load() },
      },
    },

    methods: {
      msgFromError (err, fallback) {
        return err?.response?.data?.messages?.error?.[0] ||
          err?.response?.data?.messages?.[0]?.text ||
          err?.message ||
          fallback
      },
      async load () {
        if (!this.org?.id) return
        this.loading = true
        this.loadError = ''
        this.selected = null
        try {
          const [rolesRes, catRes] = await Promise.all([
            $k.manage.clinic_one.organizations._id.cargos.get({ id: this.org.id }),
            $k.manage.clinic_one.permissions.get({}),
          ])
          this.cargos = rolesRes.cargos || []
          this.catalog = catRes.results || []
          if (this.cargos.length) this.select(this.cargos[0])
        } catch (err) {
          console.error('[roles.load]', err)
          this.loadError = this.msgFromError(err, 'Falha ao carregar cargos.')
        } finally {
          this.loading = false
        }
      },
      select (role) {
        this.selected = role
        this.form = {
          nome: role.nome,
          descricao: role.descricao || '',
          ativo: role.ativo,
          administrador: role.administrador,
          permissoes: (role.permissoes || []).map(p => ({ ...p })),
        }
      },
      findPerm (id) {
        return this.form.permissoes.find(p => p.id_permissao === id)
      },
      hasFlag (id, flag) {
        const p = this.findPerm(id)
        return p ? Boolean(p.grau_acesso & flag) : false
      },
      toggleFlag (id, flag, on) {
        const idx = this.form.permissoes.findIndex(p => p.id_permissao === id)
        if (idx === -1) {
          if (on) this.form.permissoes.push({ id_permissao: id, grau_acesso: flag })
          return
        }
        const cur = this.form.permissoes[idx].grau_acesso
        const next = on ? cur | flag : cur & ~flag
        if (next === 0) {
          this.form.permissoes.splice(idx, 1)
        } else {
          this.$set(this.form.permissoes, idx, { id_permissao: id, grau_acesso: next })
        }
      },
      countSelected (grupo) {
        return grupo.permissoes.filter(p => this.findPerm(p.id)).length
      },
      async save () {
        if (!this.selected) return
        this.saving = true
        try {
          await $k.manage.clinic_one.organizations._id.cargos._idCargo.put({
            id: this.org.id,
            idCargo: this.selected.id,
            nome: this.form.nome,
            descricao: this.form.descricao || null,
            ativo: this.form.ativo,
            administrador: this.form.administrador,
            permissoes: this.form.administrador ? [] : this.form.permissoes,
          })
          this.snackbar = { show: true, color: 'success', text: 'Cargo salvo.' }
          await this.load()
        } catch (err) {
          console.error('[roles.save]', err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao salvar.') }
        } finally {
          this.saving = false
        }
      },
      async remove (role) {
        if (!confirm(`Excluir o cargo "${role.nome}"?`)) return
        this.saving = true
        try {
          await $k.manage.clinic_one.organizations._id.cargos._idCargo.delete({ id: this.org.id, idCargo: role.id })
          this.snackbar = { show: true, color: 'success', text: 'Cargo excluído.' }
          await this.load()
        } catch (err) {
          console.error('[roles.delete]', err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao excluir.') }
        } finally {
          this.saving = false
        }
      },
      openCreateDialog () {
        this.createDialog = {
          show: true,
          saving: false,
          error: '',
          form: { nome: '', descricao: '', administrador: false },
        }
      },
      async submitCreate () {
        if (!this.createDialog.form.nome.trim()) {
          this.createDialog.error = 'Informe um nome para o cargo.'
          return
        }
        this.createDialog.saving = true
        this.createDialog.error = ''
        try {
          await $k.manage.clinic_one.organizations._id.cargos.post({
            id: this.org.id,
            nome: this.createDialog.form.nome.trim(),
            descricao: this.createDialog.form.descricao?.trim() || null,
            administrador: this.createDialog.form.administrador,
            ativo: true,
            permissoes: [],
          })
          this.snackbar = { show: true, color: 'success', text: 'Cargo criado.' }
          this.createDialog.show = false
          await this.load()
        } catch (err) {
          console.error('[roles.create]', err)
          this.createDialog.error = this.msgFromError(err, 'Falha ao criar cargo.')
        } finally {
          this.createDialog.saving = false
        }
      },
    },
  }
</script>
